import { apiFetch } from "../../../context/AuthContext";

const useAttendanceActions = ({ BASE_URL, language, getConfigForEmployeeOnDate }) => {

  // ✅ FIX: clientName and companyName are two distinct identifiers used
  // together as a composite key by companyConfigService.getConfiguration
  // (see companyConfigService.js / attendanceService.js:
  // getResolvedAttendanceConfig(employeeId, date, clientName, companyName)).
  // The previous signature only accepted a single `clientName` param, and
  // every call site below was passing `record.workingCompany` into it —
  // so the real clientName was never sent, and companyName was never sent
  // at all. For any client with more than one company (the normal case)
  // the backend couldn't find the right {clientName, companyName} row and
  // fell back to a default/wrong config, which is why the reset check-in/
  // check-out time was wrong for most employees. Both fields already exist
  // separately on the attendance record (attendanceService.js returns both
  // `clientName` and `workingCompany` on every record), so accept and send
  // them separately here.
  const getResolvedConfigFromAPI = async (employeeId, date, clientName, companyName) => {
    try {
      const queryParams = new URLSearchParams({
        date,
        clientName: clientName || '',
        companyName: companyName || '',
      });

      const response = await apiFetch(
        `${BASE_URL}/attendance/${employeeId}/resolved-config?${queryParams}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept-Language': language,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        return data.config || null;
      }
      console.warn('❌ Failed to fetch resolved config, falling back to local config');
      return null;
    } catch (error) {
      console.error('❌ Error fetching resolved config:', error);
      return null;
    }
  };

  const updateAttendancein = async (id, record, customTime = null, forceRefreshAttendance, showAlert = () => {}, t) => {
    try {
      // Try to fetch the resolved config from backend first
      let employeeConfig = await getResolvedConfigFromAPI(record.employeeId, record.date, record.clientName, record.workingCompany);
      
      // Fall back to local config if API fails
      if (!employeeConfig) {
        employeeConfig = getConfigForEmployeeOnDate(record.workingCompany, record.employeeType, record.date);
      }
      
      const resetTime = customTime || employeeConfig.workStartTime || "09:00";
      const currentViewType = localStorage.getItem('managerViewType') || 'manager';

      const [year, month, day] = record.date.split("-").map(Number);
      const [hours, minutes] = resetTime.split(":").map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const fullDatetime = localDate.toISOString();

      const response = await apiFetch(`${BASE_URL}/attendance/manual-access-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
        body: JSON.stringify({ employeeNoString: record.employeeId, date: fullDatetime, type: 'in' }),
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم تحديث وقت الدخول!" : (t.checkInUpdated || "✅ Check-in time updated!"), 'success');
      } else {
        const errorData = await response.json().catch(() => ({ error: "Invalid request" }));
        showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الدخول" : (errorData.error || t.updateCheckInFailed), 'error');
      }
    } catch (error) {
      console.error("❌ Error updating check-in:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الدخول" : (t.updateCheckInFailed || `Failed to update: ${error.message}`), 'error');
    }
  };

  const updateAttendanceout = async (id, record, customTime = null, forceRefreshAttendance, showAlert = () => {}, t) => {
    try {
      // Try to fetch the resolved config from backend first
      let employeeConfig = await getResolvedConfigFromAPI(record.employeeId, record.date, record.clientName, record.workingCompany);
      
      // Fall back to local config if API fails
      if (!employeeConfig) {
        employeeConfig = getConfigForEmployeeOnDate(record.workingCompany, record.employeeType, record.date);
      }
      
      const resetTime = customTime || employeeConfig.workEndTime || "18:00";
      const startTime = employeeConfig.workStartTime || "09:00";
      const currentViewType = localStorage.getItem('managerViewType') || 'manager';

      const [year, month, day] = record.date.split("-").map(Number);
      const [hours, minutes] = resetTime.split(":").map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      // Overnight: when the shift's workEndTime falls on the next calendar day,
      // move the reset timestamp forward by 24h so it lands on the right day.
      if (employeeConfig.isOvernightShift && resetTime < startTime) {
        localDate.setDate(localDate.getDate() + 1);
      }
      const fullDatetime = localDate.toISOString();

      const response = await apiFetch(`${BASE_URL}/attendance/manual-access-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
        body: JSON.stringify({ employeeNoString: record.employeeId, date: fullDatetime, type: 'out' }),
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم تحديث وقت الخروج!" : (t.checkOutUpdated || "✅ Check-out time updated!"), 'success');
      } else {
        const errorData = await response.json().catch(() => ({ error: "Invalid request" }));
        showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الخروج" : (errorData.error || t.updateCheckOutFailed), 'error');
      }
    } catch (error) {
      console.error("❌ Error updating check-out:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الخروج" : (t.updateCheckOutFailed || `Failed to update: ${error.message}`), 'error');
    }
  };

  const revertAttendanceToOriginal = async (record, forceRefreshAttendance, showAlert = () => {}, language) => {
    try {
      const currentViewType = localStorage.getItem('managerViewType') || 'manager';
      const response = await apiFetch(`${BASE_URL}/attendance/${record.id}/revert`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(
          language === "ar" ? "نجاح" : "Success",
          language === "ar" ? "تم استعادة البيانات الأصلية بنجاح!" : "✅ Attendance reverted to original fingerprint data!",
          'success'
        );
      } else {
        const errorData = await response.json().catch(() => ({ error: "Failed to revert" }));
        showAlert(
          language === "ar" ? "خطأ" : "Error",
          language === "ar" ? "فشل استعادة البيانات الأصلية" : (errorData.error || "❌ Failed to revert attendance"),
          'error'
        );
      }
    } catch (error) {
      console.error("❌ Error reverting attendance:", error);
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل استعادة البيانات الأصلية" : `❌ Failed to revert: ${error.message}`,
        'error'
      );
    }
  };

  return { updateAttendancein, updateAttendanceout, revertAttendanceToOriginal };
};

export default useAttendanceActions;